// useYouTubePlayer: mounts a YouTube IFrame API player and exposes
// programmatic play/pause/seek/volume so our custom controls work.
import { useEffect, useRef, useState, useCallback } from 'react';

const YT_API_URL = 'https://www.youtube.com/iframe_api';

const ensureYouTubeApi = () => new Promise((resolve) => {
  if (window.YT && window.YT.Player) return resolve(window.YT);
  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    if (typeof prev === 'function') { try { prev(); } catch (_e) { /* ignore */ } }
    resolve(window.YT);
  };
  if (!document.querySelector('script[data-yt-api]')) {
    const tag = document.createElement('script');
    tag.src = YT_API_URL;
    tag.async = true;
    tag.setAttribute('data-yt-api', '1');
    document.body.appendChild(tag);
  }
});

export default function useYouTubePlayer({ videoId, containerId, onEnded }) {
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(80);
  const pollRef = useRef(null);
  const onEndedRef = useRef(onEnded);
  useEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);

  useEffect(() => {
    let cancelled = false;
    if (!videoId || !containerId) return () => {};
    (async () => {
      const YT = await ensureYouTubeApi();
      if (cancelled) return;
      // If previous player exists, destroy it first
      try { playerRef.current && playerRef.current.destroy && playerRef.current.destroy(); } catch (_e) { /* ignore */ }
      const target = document.getElementById(containerId);
      if (!target) return;
      // Ensure the container is empty (YT.Player replaces it with an iframe)
      target.innerHTML = '';
      const wrapper = document.createElement('div');
      wrapper.id = `${containerId}-inner`;
      wrapper.style.width = '100%';
      wrapper.style.height = '100%';
      target.appendChild(wrapper);
      playerRef.current = new YT.Player(wrapper.id, {
        videoId,
        host: 'https://www.youtube-nocookie.com',
        playerVars: {
          controls: 0,           // hide native controls - we render our own
          disablekb: 1,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          playsinline: 1,
          fs: 1,
        },
        events: {
          onReady: (ev) => {
            if (cancelled) return;
            setDuration(ev.target.getDuration() || 0);
            setVolumeState(ev.target.getVolume() || 80);
            setReady(true);
          },
          onStateChange: (ev) => {
            // -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued
            const s = ev.data;
            if (s === 1) setPlaying(true);
            else if (s === 2) setPlaying(false);
            else if (s === 0) { setPlaying(false); if (onEndedRef.current) onEndedRef.current(); }
            if (s === 1 && ev.target.getDuration) {
              const d = ev.target.getDuration();
              if (d && d !== duration) setDuration(d);
            }
          },
        },
      });
    })();
    return () => {
      cancelled = true;
      try { playerRef.current && playerRef.current.destroy && playerRef.current.destroy(); } catch (_e) { /* ignore */ }
      playerRef.current = null;
      setReady(false);
      setPlaying(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, containerId]);

  // Poll currentTime while playing
  useEffect(() => {
    if (!playing) return () => {};
    pollRef.current = setInterval(() => {
      try {
        const t = playerRef.current && playerRef.current.getCurrentTime && playerRef.current.getCurrentTime();
        if (typeof t === 'number') setCurrentTime(t);
      } catch (_e) { /* ignore */ }
    }, 500);
    return () => clearInterval(pollRef.current);
  }, [playing]);

  const play = useCallback(() => { try { playerRef.current && playerRef.current.playVideo(); } catch (_e) { /* ignore */ } }, []);
  const pause = useCallback(() => { try { playerRef.current && playerRef.current.pauseVideo(); } catch (_e) { /* ignore */ } }, []);
  const seek = useCallback((sec) => {
    try { playerRef.current && playerRef.current.seekTo(sec, true); setCurrentTime(sec); } catch (_e) { /* ignore */ }
  }, []);
  const setVolume = useCallback((v) => {
    const clamped = Math.max(0, Math.min(100, Math.round(v)));
    try { playerRef.current && playerRef.current.setVolume(clamped); setVolumeState(clamped); } catch (_e) { /* ignore */ }
  }, []);
  const setPlaybackRate = useCallback((rate) => {
    try { playerRef.current && playerRef.current.setPlaybackRate(rate); } catch (_e) { /* ignore */ }
  }, []);

  return { ready, playing, currentTime, duration, volume, play, pause, seek, setVolume, setPlaybackRate };
}
